import React from 'react';
import PropTypes from 'prop-types';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';

const Pagination = ({
    currentPage,
    totalPages,
    onPageChange,
    recordsPerPage,
    onRecordsPerPageChange,
    totalRecords,
    count,
    showFirstLastControls = false,
    compact = false,
}) => {

    const handlePageChange = (pageNumber) => {
        if (pageNumber > 0 && pageNumber <= totalPages) {
            onPageChange(pageNumber);
        }
    };

    const handleRecordsPerPageChange = (e) => {
        onRecordsPerPageChange(parseInt(e.target.value, 10));
    };

    const wrapperClass = compact
        ? "flex flex-col md:flex-row justify-between items-center gap-2 p-2"
        : "flex flex-col md:flex-row justify-between items-center  gap-4 p-4 ";

    const labelClass = compact ? "mr-2 text-xs font-medium" : "mr-3 text-sm font-medium";
    const selectClass = compact
        ? "px-2 py-1 bg-white border border-gray-300 rounded-md text-xs focus:ring focus:ring-blue-300"
        : "px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:ring focus:ring-blue-300";
    const countClass = compact ? "text-xs font-medium text-gray-700" : "text-sm font-medium text-gray-700";
    const pageTextClass = compact ? "text-xs font-semibold text-gray-800" : "text-sm font-semibold text-gray-800";
    const arrowBtnClass = compact
        ? "px-2 py-1 bg-blue-500 text-white rounded-md disabled:opacity-50 hover:bg-blue-600 text-xs"
        : "px-3 p-2 bg-blue-500 text-white rounded-md disabled:opacity-50 hover:bg-blue-600";
    const iconBtnClass = compact
        ? "flex items-center px-2 py-1 bg-blue-500 text-white rounded-md disabled:opacity-50 hover:bg-blue-600"
        : "flex items-center px-4 p-2  bg-blue-500 text-white rounded-md disabled:opacity-50 hover:bg-blue-600";
    const leftIconClass = compact ? "h-4 w-4" : "mr-2 h-4 w-4";
    const rightIconClass = compact ? "h-4 w-4" : "ml-2 h-8 w-8";

    return (
        <div className={wrapperClass}>
            {/* Records per page dropdown */}
            <div className="flex items-center">
                <label htmlFor="records-per-page" className={labelClass}>
                    Records per page:
                </label>
                <select
                    id="records-per-page"
                    value={recordsPerPage}
                    onChange={handleRecordsPerPageChange}
                    className={selectClass}
                >
                    <option value={10}>10</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={250}>250</option>
                    <option value={500}>500</option>
                    <option value={1000}>1000</option>
                </select>
            </div>

            {/* Total records */}
            <div className={countClass}>
                <span className="font-semibold">{count}/{totalRecords}</span>
            </div>

            {/* Pagination controls */}
            <div className={`flex items-center ${compact ? "gap-2" : "gap-4"}`}>
                {showFirstLastControls && (
                    <div className="flex items-center gap-1">
                        <button
                            onClick={() => handlePageChange(1)}
                            disabled={currentPage === 1}
                            className={arrowBtnClass}
                            title="First page"
                            aria-label="First page"
                        >
                            {'<<'}
                        </button>
                        <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className={iconBtnClass}
                            title="Previous page"
                            aria-label="Previous page"
                        >
                            <ChevronLeft className={leftIconClass} />
                        </button>
                    </div>
                )}
                {!showFirstLastControls && (
                    <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={iconBtnClass}
                    >
                        <ChevronLeft className={leftIconClass} />
                    </button>
                )}
                <div className={pageTextClass}>
                    {currentPage} / {totalPages}
                </div>
                {!showFirstLastControls && (
                    <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={iconBtnClass}
                    >
                        <ChevronRight className={rightIconClass} />
                    </button>
                )}
                {showFirstLastControls && (
                    <div className="flex items-center gap-1">
                        <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className={iconBtnClass}
                            title="Next page"
                            aria-label="Next page"
                        >
                            <ChevronRight className={rightIconClass} />
                        </button>
                        <button
                            onClick={() => handlePageChange(totalPages)}
                            disabled={currentPage === totalPages}
                            className={arrowBtnClass}
                            title="Last page"
                            aria-label="Last page"
                        >
                            {'>>'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

Pagination.propTypes = {
    currentPage: PropTypes.number.isRequired,
    totalPages: PropTypes.number.isRequired,
    onPageChange: PropTypes.func.isRequired,
    recordsPerPage: PropTypes.number.isRequired,
    onRecordsPerPageChange: PropTypes.func.isRequired,
    totalRecords: PropTypes.number.isRequired,
    count: PropTypes.number.isRequired,
    showFirstLastControls: PropTypes.bool,
    compact: PropTypes.bool,
};

export default Pagination;
